import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as LightweightCharts from 'lightweight-charts';
import { getChartData } from '../../services/api';
import type { ChartData } from '../../types';
import { acquire, release } from '../../utils/requestPool';
import type { OHLCData } from './CandlestickChart';
import {
  getChartColors,
  getMaColors,
  resolveMaPeriodsToRender,
  MA_PERIODS,
  type MAPeriod,
} from '../../constants/chartColors';
import { useAppSelector } from '../../store';
import { useTheme } from '../../contexts/ThemeContext';

const CandlestickSeries = (LightweightCharts as any).CandlestickSeries;
const LineSeries = (LightweightCharts as any).LineSeries;
const createChart = LightweightCharts.createChart;
const ColorType = LightweightCharts.ColorType;

// Module-level LRU cache
const CACHE_TTL_MS = 5 * 60 * 1000;
const MAX_CACHE_SIZE = 200;
const chartCache = new Map<string, { data: ChartData; ts: number }>();

/** Key cache by ticker + timeframe so weekly/daily flips don't serve stale bars. */
function gridCacheKey(ticker: string, timeframe: string): string {
  return `${ticker}:${timeframe}`;
}

function getCached(ticker: string, timeframe: string): ChartData | null {
  const key = gridCacheKey(ticker, timeframe);
  const entry = chartCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL_MS) {
    chartCache.delete(key);
    return null;
  }
  chartCache.delete(key);
  chartCache.set(key, entry);
  return entry.data;
}

function setCache(ticker: string, timeframe: string, data: ChartData) {
  if (chartCache.size >= MAX_CACHE_SIZE) {
    const firstKey = chartCache.keys().next().value;
    if (firstKey !== undefined) chartCache.delete(firstKey);
  }
  chartCache.set(gridCacheKey(ticker, timeframe), { data, ts: Date.now() });
}

function chartToOHLC(chartData: ChartData): OHLCData[] {
  if (!chartData?.ohlc?.length) return [];
  const dates = chartData.dates || [];
  const ohlc = chartData.ohlc;

  const generateFallbackDate = (index: number, totalLength: number): string => {
    const baseDate = new Date();
    baseDate.setUTCHours(0, 0, 0, 0);
    baseDate.setUTCDate(baseDate.getUTCDate() - (totalLength - 1 - index));
    return baseDate.toISOString().split('T')[0] as string;
  };

  const getDate = (index: number): string => {
    const d = dates[index];
    if (d && typeof d === 'string') return d;
    return generateFallbackDate(index, ohlc.length);
  };

  return ohlc.map((item, index) => ({
    date: getDate(index),
    open: item.o,
    high: item.h,
    low: item.l,
    close: item.c,
    volume: item.v ?? 0,
  }));
}

type SparseSeries = Array<{ x: number; y: number }> | undefined;

function sparseMaToAlignedSeries(
  ohlcLength: number,
  series: SparseSeries
): (number | null)[] {
  const out: (number | null)[] = Array(ohlcLength).fill(null);
  if (!series?.length) return out;
  for (const p of series) {
    if (p.x >= 0 && p.x < ohlcLength) {
      out[p.x] = p.y;
    }
  }
  return out;
}

/** Period → field-name lookup on the ChartData payload. */
const MA_FIELD: Record<MAPeriod, keyof ChartData> = {
  5: 'ma_5',
  10: 'ma_10',
  20: 'ma_20',
  50: 'ma_50',
  100: 'ma_100',
  150: 'ma_150',
  200: 'ma_200',
};

function chartToMaData(chartData: ChartData): {
  [period: number]: (number | null)[];
} {
  const n = chartData.ohlc?.length ?? 0;
  if (n === 0) return {};
  const out: { [period: number]: (number | null)[] } = {};
  for (const period of MA_PERIODS) {
    const series = chartData[MA_FIELD[period]] as SparseSeries;
    if (series?.length) {
      out[period] = sparseMaToAlignedSeries(n, series);
    }
  }
  return out;
}

/** Backend grid already caps (~252); avoid extra truncation so ~1Y daily is visible. */
const GRID_DAILY_DISPLAY_CAP = 320;
const PREVIEW_INTRADAY_MAX_BARS = 96;

function tailPreview<T>(arr: T[], max: number): T[] {
  if (arr.length <= max) return arr;
  return arr.slice(-max);
}

/** Full bar time in seconds (intraday ISO strings need this; UTC-midnight-only collapses all same-day bars). */
function ohlcDateToUnixSeconds(dateStr: string): number {
  const ms = new Date(dateStr).getTime();
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : NaN;
}

type CandleRow = {
  time: LightweightCharts.UTCTimestamp;
  open: number;
  high: number;
  low: number;
  close: number;
};

/** v5 setData requires strictly ascending time; merge duplicate seconds into one OHLC bar. */
function mergeCandlesStrictAscending(rows: CandleRow[]): CandleRow[] {
  const normalized = rows
    .map((r) => ({
      t: Math.trunc(Number(r.time)),
      open: r.open,
      high: r.high,
      low: r.low,
      close: r.close,
    }))
    .filter((r) => Number.isFinite(r.t));
  normalized.sort((a, b) => a.t - b.t);
  const out: CandleRow[] = [];
  for (const r of normalized) {
    const prev = out[out.length - 1];
    if (prev && Math.trunc(Number(prev.time)) === r.t) {
      prev.high = Math.max(prev.high, r.high);
      prev.low = Math.min(prev.low, r.low);
      prev.close = r.close;
    } else {
      out.push({
        time: r.t as LightweightCharts.UTCTimestamp,
        open: r.open,
        high: r.high,
        low: r.low,
        close: r.close,
      });
    }
  }
  return out;
}

type LineRow = { time: LightweightCharts.UTCTimestamp; value: number };

function mergeLineStrictAscending(points: LineRow[]): LineRow[] {
  const normalized = points
    .map((p) => ({ t: Math.trunc(Number(p.time)), value: p.value }))
    .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.value));
  normalized.sort((a, b) => a.t - b.t);
  const out: LineRow[] = [];
  for (const p of normalized) {
    const prev = out[out.length - 1];
    if (prev && Math.trunc(Number(prev.time)) === p.t) {
      prev.value = p.value;
    } else {
      out.push({ time: p.t as LightweightCharts.UTCTimestamp, value: p.value });
    }
  }
  return out;
}

export interface ChartsGridLightweightCellProps {
  ticker: string;
  /** When false (e.g. Charts tab uses `hidden`), skip chart init - avoids 0×0 layout and squashed first paint */
  isGridVisible?: boolean;
}

const ChartsGridLightweightCell: React.FC<ChartsGridLightweightCellProps> = ({
  ticker,
  isGridVisible = true,
}) => {
  /** Read selection + timeframe from Redux; regression-safe fallback to MA_150+MA_200 via resolveMaPeriodsToRender. */
  const maFilterSelection = useAppSelector(
    (state) => state.filters.ma_filter_selection
  );
  const maTimeframeMode = useAppSelector(
    (state) => state.filters.ma_timeframe_mode ?? 'DAILY'
  );
  const { theme: themeMode } = useTheme();
  const themedColors = useMemo(() => getChartColors(themeMode), [themeMode]);
  // Use the same MA palette as every other chart in the app for full token
  // parity (Charts grid mini-charts → Live Chart → Deep Analysis chart).
  const themedMaColors = useMemo(() => getMaColors(themeMode), [themeMode]);

  const [raw, setRaw] = useState<ChartData | null>(() =>
    getCached(ticker, maTimeframeMode)
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!getCached(ticker, maTimeframeMode));
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<LightweightCharts.IChartApi | null>(null);
  const candlestickSeriesRef = useRef<any>(null);
  /** Selection-driven: one series per MA period (keyed by numeric period). */
  const maSeriesRefs = useRef<Map<MAPeriod, any>>(new Map());
  const periodsToRender = useMemo(
    () => resolveMaPeriodsToRender(maFilterSelection),
    [maFilterSelection]
  );
  const cleanupRef = useRef<{
    ro: ResizeObserver;
    chart: LightweightCharts.IChartApi;
    resizeRaf: { id: number };
  } | null>(null);
  /** Bumped after createChart + addSeries (rAF); data effect runs in same commit before refs exist without this. */
  const [chartReadyGeneration, setChartReadyGeneration] = useState(0);

  useEffect(() => {
    const cached = getCached(ticker, maTimeframeMode);
    if (cached) {
      setRaw(cached);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let released = false;
    setLoading(true);
    setError(null);

    Promise.resolve(acquire())
      .then(() => {
        if (controller.signal.aborted) {
          released = true;
          release();
          return;
        }
        return getChartData(ticker, maTimeframeMode, controller.signal, {
          grid: true,
        })
          .then((d) => {
            if (!controller.signal.aborted) {
              setCache(ticker, maTimeframeMode, d);
              setRaw(d);
            }
          })
          .catch((e: Error) => {
            if (!controller.signal.aborted) {
              setError(e.message || 'Failed to load chart');
              setRaw(null);
            }
          })
          .finally(() => {
            if (!released) {
              released = true;
              release();
            }
            if (!controller.signal.aborted) {
              setLoading(false);
            }
          });
      })
      .catch((e: unknown) => {
        if (!controller.signal.aborted) {
          setError(e instanceof Error ? e.message : 'Request pool error');
          setLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [ticker, maTimeframeMode]);

  const ohlcFull = useMemo(() => {
    if (!raw) return [];
    try {
      return chartToOHLC(raw);
    } catch {
      return [];
    }
  }, [raw]);

  const maDataFull = useMemo(() => {
    if (!raw) return {};
    try {
      return chartToMaData(raw);
    } catch {
      return {};
    }
  }, [raw]);

  const previewBarCap =
    raw?.preview_source === 'intraday'
      ? PREVIEW_INTRADAY_MAX_BARS
      : GRID_DAILY_DISPLAY_CAP;

  const ohlc = useMemo(
    () => tailPreview(ohlcFull, previewBarCap),
    [ohlcFull, previewBarCap]
  );

  const maData = useMemo(() => {
    const n = ohlcFull.length;
    if (n === 0) return {};
    const k = Math.min(n, previewBarCap);
    const out: { [period: number]: (number | null)[] } = {};
    const tailMaAligned = (aligned: (number | null)[] | undefined) => {
      if (!aligned || aligned.length !== n) return undefined;
      if (n <= k) return aligned;
      return aligned.slice(n - k);
    };
    // Slice only the periods the user selected (regression-safe fallback: [150, 200]).
    for (const period of periodsToRender) {
      const aligned = tailMaAligned(maDataFull[period]);
      if (aligned?.length) out[period] = aligned;
    }
    return out;
  }, [ohlcFull, maDataFull, previewBarCap, periodsToRender]);

  /** Chart div only mounts after load; init effect must re-run when this becomes true (not on mount []). */
  const chartMountReady =
    !loading && !error && ohlc.length > 0 && isGridVisible;

  // Initialize chart - same config as CandlestickChart for gridCell
  useEffect(() => {
    let rafId: number | undefined;

    const tearDownChart = () => {
      if (rafId !== undefined) cancelAnimationFrame(rafId);
      if (cleanupRef.current) {
        if (cleanupRef.current.resizeRaf.id) {
          cancelAnimationFrame(cleanupRef.current.resizeRaf.id);
          cleanupRef.current.resizeRaf.id = 0;
        }
        cleanupRef.current.ro.disconnect();
        cleanupRef.current.chart.remove();
        cleanupRef.current = null;
      }
      chartRef.current = null;
      candlestickSeriesRef.current = null;
      maSeriesRefs.current.clear();
    };

    if (!chartMountReady) {
      tearDownChart();
      return;
    }

    if (!containerRef.current || chartRef.current) return;

    const container = containerRef.current;

    // Use requestAnimationFrame to ensure container has been laid out
    const initChart = () => {
      const dpr =
        typeof window !== 'undefined'
          ? Math.min(window.devicePixelRatio || 1, 1.5)
          : 1;

      const w = container.clientWidth || 300;
      const h = container.clientHeight || 280;

      if (w < 10 || h < 10) {
        // Container not laid out yet, retry next frame
        rafId = requestAnimationFrame(initChart);
        return;
      }

      const chart = createChart(container, {
        width: w,
        height: h,
        devicePixelRatio: dpr,
        layout: {
          background: { type: ColorType.Solid, color: themedColors.BACKGROUND },
          textColor: themedColors.TEXT,
          fontSize: 13,
          fontFamily:
            'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
          attributionLogo: false,
        },
        grid: {
          vertLines: { color: themedColors.GRID, style: 1, visible: false },
          horzLines: { color: themedColors.GRID, style: 1, visible: true },
        },
        rightPriceScale: {
          visible: true,
          borderColor: themedColors.GRID,
          /* Extra bottom margin so candle bodies + time-axis labels stay inside small grid cells */
          scaleMargins: { top: 0.06, bottom: 0.22 },
          autoScale: true,
        },
        timeScale: {
          borderColor: themedColors.GRID,
          timeVisible: false,
          secondsVisible: false,
          rightOffset: 5,
          tickMarkFormatter: (time: LightweightCharts.UTCTimestamp) => {
            const date = new Date((time as number) * 1000);
            const day = date.getUTCDate();
            const month = date.toLocaleString('en-US', {
              month: 'short',
              timeZone: 'UTC',
            });
            const y = date.getUTCFullYear();
            return `${day} ${month} '${String(y).slice(-2)}`;
          },
        },
        crosshair: {
          mode: 3, // Hidden for grid cells
          vertLine: {
            color: 'transparent',
            style: 0,
            labelBackgroundColor: 'transparent',
          },
          horzLine: {
            color: 'transparent',
            style: 0,
            labelBackgroundColor: 'transparent',
          },
        },
        handleScroll: false,
        handleScale: false,
      } as any);

      chartRef.current = chart;

      // ResizeObserver: resize + refit time scale (width-only applyOptions leaves empty gutter on first layout)
      const resizeRaf = { id: 0 };
      const ro = new ResizeObserver(() => {
        if (!containerRef.current || !chartRef.current) return;
        if (resizeRaf.id) cancelAnimationFrame(resizeRaf.id);
        resizeRaf.id = requestAnimationFrame(() => {
          resizeRaf.id = 0;
          if (!containerRef.current || !chartRef.current) return;
          const rw = Math.floor(
            containerRef.current.getBoundingClientRect().width
          );
          const rh = Math.floor(
            containerRef.current.getBoundingClientRect().height
          );
          if (rw > 0 && rh > 0) {
            chartRef.current.applyOptions({ width: rw, height: rh });
            chartRef.current.timeScale().fitContent();
            try {
              (chartRef.current as any)
                .priceScale?.('right')
                ?.applyOptions?.({ autoScale: true });
            } catch {
              /* ignore */
            }
          }
        });
      });
      ro.observe(container);

      const chartV5 = chart as any;

      // Candlestick series - same config as CandlestickChart
      const candlestickSeries = chartV5.addSeries(CandlestickSeries, {
        upColor: themedColors.BULLISH,
        downColor: themedColors.BEARISH,
        borderUpColor: themedColors.BULLISH,
        borderDownColor: themedColors.BEARISH,
        wickUpColor: themedColors.WICK_BULLISH,
        wickDownColor: themedColors.WICK_BEARISH,
        priceLineVisible: false,
        lastValueVisible: true,
      });
      candlestickSeriesRef.current = candlestickSeries;

      // Store cleanup ref
      cleanupRef.current = { ro, chart, resizeRaf };

      setChartReadyGeneration((g) => g + 1);
    };

    rafId = requestAnimationFrame(initChart);

    return tearDownChart;
    // themedColors dep: recreate chart on theme toggle so lightweight-charts
    // pick up the new background/grid/candle palette (cheap - one toggle per cell).
  }, [chartMountReady, themedColors]);

  /** Tab just became visible: re-apply size + fit - fixes half-width / empty-left after `hidden` → shown */
  useEffect(() => {
    if (!isGridVisible) return undefined;
    let raf2 = 0;
    const run = () => {
      if (!containerRef.current || !chartRef.current) return;
      const rw = Math.floor(containerRef.current.getBoundingClientRect().width);
      const rh = Math.floor(
        containerRef.current.getBoundingClientRect().height
      );
      if (rw < 10 || rh < 10) return;
      chartRef.current.applyOptions({ width: rw, height: rh });
      chartRef.current.timeScale().fitContent();
      try {
        (chartRef.current as any)
          .priceScale?.('right')
          ?.applyOptions?.({ autoScale: true });
      } catch {
        /* ignore */
      }
    };
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(run);
    });
    const t = window.setTimeout(run, 100);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(t);
    };
    // themedColors / themeMode dep: recreate chart on theme toggle so lightweight-charts
    // pick up new background/grid/candle colors. Cheap (one-time per toggle, grid cell).
  }, [isGridVisible, chartReadyGeneration, themedColors]);

  // Update data
  useEffect(() => {
    if (!chartRef.current || !candlestickSeriesRef.current || ohlc.length === 0)
      return;

    const formatCandlestickData = (data: OHLCData[]): CandleRow[] => {
      const mapped: CandleRow[] = [];
      for (const item of data) {
        const timestamp = ohlcDateToUnixSeconds(item.date);
        if (!Number.isFinite(timestamp)) continue;
        mapped.push({
          time: timestamp as LightweightCharts.UTCTimestamp,
          open: item.open,
          high: item.high,
          low: item.low,
          close: item.close,
        });
      }
      return mergeCandlesStrictAscending(mapped);
    };

    candlestickSeriesRef.current.setData(formatCandlestickData(ohlc));
    chartRef.current.timeScale().fitContent();
    try {
      const chartApi = chartRef.current as any;
      chartApi.priceScale?.('right')?.applyOptions?.({ autoScale: true });
    } catch {
      /* ignore */
    }

    const chartV5 = chartRef.current as any;

    // Remove existing MA series (selection may have changed - tear all down, re-add only the selected).
    maSeriesRefs.current.forEach((series) => {
      try {
        chartV5.removeSeries(series);
      } catch {
        /* ignore */
      }
    });
    maSeriesRefs.current.clear();

    if (ohlc.length === 0) return;

    const formatLineData = (
      data: OHLCData[],
      values: (number | null)[]
    ): LineRow[] =>
      mergeLineStrictAscending(
        data
          .map((item, i) => {
            const timestamp = ohlcDateToUnixSeconds(item.date);
            const value = values[i];
            if (
              !Number.isFinite(timestamp) ||
              value == null ||
              !Number.isFinite(value)
            )
              return null;
            return { time: timestamp as LightweightCharts.UTCTimestamp, value };
          })
          .filter((item): item is LineRow => item !== null)
      );

    // Render selected MAs; regression-safe fallback (legacy MA_150+MA_200) already applied in periodsToRender.
    for (const period of periodsToRender) {
      const values = maData[period];
      if (!values?.length) continue;
      const series = chartV5.addSeries(LineSeries, {
        color: themedMaColors[period],
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
      });
      series.setData(formatLineData(ohlc, values));
      maSeriesRefs.current.set(period, series);
    }
  }, [ohlc, maData, chartReadyGeneration, periodsToRender, themedMaColors]);

  if (loading) {
    return (
      <div className="w-full h-full min-h-[200px] rounded bg-surface-container-low animate-pulse border border-border-subtle flex items-center justify-center">
        <span className="text-xs text-text-muted">Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full min-h-[160px] flex items-center justify-center p-2 text-center text-xs text-danger bg-surface-container-low">
        <div>
          <div className="font-bold mb-1">Chart Error</div>
          <div>{error}</div>
          <div className="text-[10px] text-text-muted mt-1">
            Ticker: {ticker}
          </div>
        </div>
      </div>
    );
  }

  if (ohlc.length === 0) {
    return (
      <div className="w-full h-full min-h-[160px] flex items-center justify-center p-2 text-center text-xs text-secondary">
        No OHLC data for {ticker}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-full min-h-0 w-full min-w-0 overflow-hidden rounded border border-border-subtle bg-surface-container-low"
    />
  );
};

export default React.memo(ChartsGridLightweightCell);
